/**
 * 阿里云百炼 · 万相 wan2.2-animate-move（动作模仿）
 * 文档：https://help.aliyun.com/zh/model-studio/wan-animate-move-api
 * 经后端 /wan_animate_move 代理（DashScope 原生，不经 API易）
 */

import { resolveApiUrl } from './apiBaseUrl'

export const WAN_ANIMATE_MOVE_MODEL = 'wan2.2-animate-move'

export const WAN_ANIMATE_MOVE_MODES = [
  { id: 'wan-std', label: '标准模式' },
  { id: 'wan-pro', label: '专业模式' }
]

function getAuthHeader() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
}

function parseJsonSafe(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * Dingo 等常把业务字段包在 data / data.data 多层里，需解包后再读 status / video_url
 */
function unwrapApiPayload(res) {
  let cur = res
  for (let i = 0; i < 6; i++) {
    if (!cur || typeof cur !== 'object') break
    if (
      Object.prototype.hasOwnProperty.call(cur, 'video_url') ||
      Object.prototype.hasOwnProperty.call(cur, 'result_url') ||
      Object.prototype.hasOwnProperty.call(cur, 'status') ||
      Object.prototype.hasOwnProperty.call(cur, 'task_id') ||
      Object.prototype.hasOwnProperty.call(cur, 'id')
    ) {
      // 若同时有 data 且 data 里才有视频地址，继续深入
      const nested = cur.data
      if (
        nested &&
        typeof nested === 'object' &&
        !pickVideoUrlShallow(cur) &&
        pickVideoUrlShallow(nested)
      ) {
        cur = nested
        continue
      }
      if (
        nested &&
        typeof nested === 'object' &&
        (cur.status == null || cur.status === '') &&
        nested.status != null
      ) {
        cur = nested
        continue
      }
      break
    }
    if (cur.data && typeof cur.data === 'object') {
      cur = cur.data
      continue
    }
    break
  }
  return cur && typeof cur === 'object' ? cur : res
}

function pickVideoUrlShallow(info) {
  if (!info || typeof info !== 'object') return ''
  const direct =
    info.video_url ||
    info.result_url ||
    info.video_public_url ||
    info.upstream_video_url ||
    ''
  if (direct && String(direct).trim()) return String(direct).trim()
  if (info.output && typeof info.output === 'object') {
    const o = info.output
    const fromOut =
      o.video_url ||
      o.result_url ||
      (o.results && (o.results.video_url || o.results.result_url)) ||
      ''
    if (fromOut && String(fromOut).trim()) return String(fromOut).trim()
  }
  return ''
}

function pickTaskId(data) {
  const root = unwrapApiPayload(data)
  if (!root || typeof root !== 'object') return ''
  for (const k of ['id', 'task_id', 'taskId']) {
    const v = root[k]
    if (v != null && String(v).trim()) return String(v).trim()
  }
  if (root.output && typeof root.output === 'object') {
    const v = root.output.task_id || root.output.taskId
    if (v != null && String(v).trim()) return String(v).trim()
  }
  if (data && data.raw && typeof data.raw === 'object') return pickTaskId(data.raw)
  return ''
}

function pickVideoUrl(info) {
  if (!info || typeof info !== 'object') return ''
  const unwrapped = unwrapApiPayload(info)
  return pickVideoUrlShallow(unwrapped) || pickVideoUrlShallow(info)
}

function pickStatus(info) {
  if (!info || typeof info !== 'object') return ''
  const unwrapped = unwrapApiPayload(info)
  let status = unwrapped.status != null ? unwrapped.status : info.status
  if ((status == null || status === '') && unwrapped.output && unwrapped.output.task_status != null) {
    status = unwrapped.output.task_status
  }
  return status != null ? String(status).toLowerCase() : ''
}

function normalizeMode(mode) {
  const m = String(mode || 'wan-std')
    .trim()
    .toLowerCase()
  return m === 'wan-pro' ? 'wan-pro' : 'wan-std'
}

/**
 * @param {Object} opts
 * @param {string} opts.imageUrl 人物图片公网 URL
 * @param {string} opts.videoUrl 参考动作视频公网 URL
 * @param {string} [opts.mode] wan-std | wan-pro
 * @param {boolean} [opts.watermark]
 * @param {boolean} [opts.checkImage]
 * @param {number} [opts.duration] 预估秒数（用于积分）
 * @param {AbortSignal} [opts.signal]
 */
export async function createWanAnimateMoveTask(opts) {
  const {
    imageUrl,
    videoUrl,
    mode,
    watermark,
    checkImage,
    duration,
    signal
  } = opts || {}
  const img = String(imageUrl || '').trim()
  const vid = String(videoUrl || '').trim()
  if (!img) throw new Error('请提供人物图片')
  if (!vid) throw new Error('请提供参考动作视频')

  const res = await fetch(resolveApiUrl('/wan_animate_move'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    body: JSON.stringify({
      image_url: img,
      video_url: vid,
      mode: normalizeMode(mode),
      ...(watermark != null ? { watermark: !!watermark } : {}),
      ...(checkImage != null ? { check_image: !!checkImage } : {}),
      ...(duration != null && !Number.isNaN(Number(duration))
        ? { duration: Math.min(30, Math.max(1, Math.round(Number(duration)))) }
        : {})
    }),
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || { raw: text }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error || data.errors)) ||
      text.slice(0, 400) ||
      `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  const taskId = pickTaskId(data)
  if (!taskId) {
    throw new Error('创建动作模仿任务成功但未解析到 task_id：' + text.slice(0, 400))
  }
  return { ...unwrapApiPayload(data), id: taskId, raw: data }
}

export async function getWanAnimateMoveTask(taskId, signal) {
  const id = encodeURIComponent(String(taskId || '').trim())
  if (!id) throw new Error('缺少 task_id')
  const res = await fetch(resolveApiUrl(`/wan_animate_move/${id}`), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || {}
  if (!res.ok) {
    const msg = data.message || data.error || text.slice(0, 400) || `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  const inner = unwrapApiPayload(data)
  const videoUrl = pickVideoUrl(inner) || pickVideoUrl(data)
  const status = pickStatus(inner) || pickStatus(data)
  return {
    ...inner,
    id: inner.id || inner.task_id || taskId,
    task_id: inner.task_id || inner.id || taskId,
    status,
    video_url: videoUrl || inner.video_url,
    result_url: inner.result_url || videoUrl,
    raw: data
  }
}

export function pickWanAnimateMoveVideoUrl(info) {
  return pickVideoUrl(info)
}

export async function pollWanAnimateMoveUntilVideo(taskId, opts) {
  const maxAttempts = (opts && opts.maxAttempts) || 240
  const intervalMs = (opts && opts.intervalMs) || 5000
  for (let i = 0; i < maxAttempts; i++) {
    const info = await getWanAnimateMoveTask(String(taskId))
    const st = String(info.status || '').toLowerCase()
    const videoUrl = pickWanAnimateMoveVideoUrl(info)
    if (videoUrl) return videoUrl
    if (st === 'failed' || st === 'failure' || st === 'error') {
      throw new Error(
        (info && (info.message || info.error))
          ? String(info.message || info.error)
          : '动作模仿任务失败'
      )
    }
    if (i < maxAttempts - 1) {
      await new Promise(r => setTimeout(r, intervalMs))
    }
  }
  throw new Error('等待动作模仿视频超时，请稍后重试')
}
